import React, { useMemo, useState } from 'react';
import { AddressReportResponse } from '../../services/addressManagementService';
import ComparisonMap from './ComparisonMap';
import './AddressReportBeta.css';
import GeminiService from '../../services/geminiService';
import { toast } from 'react-toastify';

interface AddressReportManagementProps {
    data: AddressReportResponse | null;
    isLoading: boolean;
}

const AddressReportManagement: React.FC<AddressReportManagementProps> = ({ data, isLoading }) => {
    const [summary, setSummary] = useState<string | null>(null);
    const [isSummarizing, setIsSummarizing] = useState(false);

    // Parser helpers
    const parsedGoogleResponse = useMemo(() => {
        if (!data?.addressValidationInfo?.googleResponse) return null;
        try {
            return JSON.parse(data.addressValidationInfo.googleResponse);
        } catch (e) {
            console.error("Failed to parse googleResponse", e);
            return null;
        }
    }, [data]);

    const parsedRadarResponse = useMemo(() => {
        if (!data?.addressValidationInfo?.radarResponse) return null;
        try {
            return JSON.parse(data.addressValidationInfo.radarResponse);
        } catch (e) {
            console.error("Failed to parse radarResponse", e);
            return null;
        }
    }, [data]);

    const parsedDestinationsResponse = useMemo(() => {
        if (!data?.addressValidationInfo?.googleGeocodeDestinationsResponse) return null;
        try {
            return JSON.parse(data.addressValidationInfo.googleGeocodeDestinationsResponse);
        } catch (e) {
            console.error("Failed to parse destinationsResponse", e);
            return null;
        }
    }, [data]);

    // Calculate Delta (Haversine formula)
    const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
        const R = 6371e3; // metres
        const φ1 = lat1 * Math.PI / 180;
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;

        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    };

    const deltaInfo = useMemo(() => {
        if (data?.address?.latitude && data?.address?.longitude && data?.address?.radarLatitude && data?.address?.radarLongitude) {
            const distMeters = calculateDistance(data.address.latitude, data.address.longitude, data.address.radarLatitude, data.address.radarLongitude);
            const distFeet = distMeters * 3.28084;
            const dLat = Math.abs(data.address.latitude - data.address.radarLatitude);
            const dLon = Math.abs(data.address.longitude - data.address.radarLongitude);
            return {
                meters: distMeters.toFixed(2),
                feet: distFeet.toFixed(2),
                dLat: dLat.toFixed(7),
                dLon: dLon.toFixed(7)
            };
        }
        return null;
    }, [data]);

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Generating Report...</p>
            </div>
        );
    }

    if (!data) return <div className="p-4 text-center text-muted">No report data available.</div>;

    const { address } = data;
    const uspsData = parsedGoogleResponse?.Result?.UspsData;
    const googleComponents = parsedGoogleResponse?.Result?.Address?.AddressComponents;
    const radarData = parsedRadarResponse?.RadarValidateResponse?.Address;
    const navPoint = parsedDestinationsResponse?.destinations?.[0]?.navigationPoints?.[0];

    // Helper to find google address component
    const getGoogleComponent = (type: string) => {
        return googleComponents?.find((c: any) => c.ComponentType === type)?.ComponentName?.Text;
    };

    const zipSuffix = address.zipSuffix || getGoogleComponent('postal_code_suffix');
    const fullZip = zipSuffix ? `${address.zip}-${zipSuffix}` : address.zip;

    const mapZoom = address.geoFenceConfidence === 'HIGH' ? 20 : 19;

    const anonymizeSource = (source: string | null | undefined) => {
        if (!source) return 'N/A';
        return source
            .replace(/GOOGLE/g, 'Provider 01') // User requested 'Provider 01' specifically
            .replace(/RADAR/g, 'Provider 2')
            .replace(/_/g, ' & '); // Make compound sources look nicer
    };

    const handleSummarize = async () => {
        if (!data) return;
        setIsSummarizing(true);
        try {
            const result = await GeminiService.summarizeAddressReport(data);
            setSummary(result);
            // Scroll to bottom to see summary
            setTimeout(() => {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }, 100);
        } catch (error) {
            console.error(error);
            toast.error("Failed to generate summary. Please check your API key.");
        } finally {
            setIsSummarizing(false);
        }
    };

    return (
        <div className="address-report-beta">
            <div className="report-page">
                {/* TOP BAR */}
                <div className="topbar">
                    <div className="topbar-left">
                        <div className="logo-mark">BetterTrucks</div>
                        <div className="logo-sep"></div>
                        <div className="topbar-label">Checkpoint - Address Validation Report</div>
                    </div>
                    <div className="topbar-right">
                        {new Date().toLocaleDateString()} · {new Date().toLocaleTimeString()} UTC<br />
                        ID #{address.id} · Source: {anonymizeSource(address.validationSource)}
                    </div>
                </div>

                {/* HERO */}
                <div className="hero">
                    <div>
                        <div className="hero-address">
                            {address.street1}<br />
                            {address.street2 && <>{address.street2}<br /></>}
                            {address.city}, {address.stateCode} {fullZip}
                        </div>
                        <div className="hero-sub">
                            {uspsData?.County?.toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase())} County · {address.country} · Trace: {address.traceId}
                        </div>
                    </div>
                    <div className="hero-badges">
                        {address.isValid && (
                            <span className="chip chip-valid"><span className="dot dot-v"></span> Valid & Deliverable</span>
                        )}
                        <span className="chip chip-blue"><span className="dot" style={{ backgroundColor: '#34a851' }}></span> Provider 1: {address.googlePossibleNextAction || 'N/A'}</span>
                        <span className="chip chip-teal"><span className="dot" style={{ backgroundColor: '#000257' }}></span> Provider 2: {address.isRadarValid ? 'Verified' : 'Unverified'}</span>
                    </div>
                </div>

                {/* BODY */}
                <div className="body">
                    {/* COORDINATES */}
                    <div className="section-head">Geocoordinates</div>
                    <div className="coord-grid mb">
                        {/* Google */}
                        <div className="coord-cell">
                            <div className="coord-cell-head"><span className="dot" style={{ backgroundColor: '#34a851' }}></span> Provider 1 Validation API</div>
                            <div className="coord-pair-row">
                                <div className="coord-field">
                                    <label>Latitude</label>
                                    <div className="val">{address.latitude}</div>
                                </div>
                                <div className="coord-field">
                                    <label>Longitude</label>
                                    <div className="val">{address.longitude}</div>
                                </div>
                            </div>
                            <div className="coord-meta">
                                {parsedGoogleResponse?.Result?.Geocode?.LocationType && (
                                    <span style={{ background: 'var(--accent-bg)', color: 'var(--google)', padding: '.1rem .4rem', fontSize: '.62rem', fontFamily: "'DM Mono',monospace", borderRadius: '2px' }}>
                                        {parsedGoogleResponse.Result.Geocode.LocationType}
                                    </span>
                                )}
                                Granularity: {parsedGoogleResponse?.Result?.Verdict?.GeocodeGranularity || 'N/A'}
                            </div>
                            <a className="maps-link" href={`https://maps.google.com/?q=${address.latitude},${address.longitude}`} target="_blank" rel="noreferrer">
                                ↗ Open in Google Maps
                            </a>
                        </div>

                        {/* Radar */}
                        <div className="coord-cell">
                            <div className="coord-cell-head"><span className="dot" style={{ backgroundColor: '#000257' }}></span> Provider 2 Forward Geocode</div>
                            <div className="coord-pair-row">
                                <div className="coord-field">
                                    <label>Latitude</label>
                                    <div className="val">{address.radarLatitude}</div>
                                </div>
                                <div className="coord-field">
                                    <label>Longitude</label>
                                    <div className="val">{address.radarLongitude}</div>
                                </div>
                            </div>
                            <div className="coord-meta">
                                {parsedRadarResponse?.RadarForwardResponse?.Addresses?.[0]?.Confidence && (
                                    <span style={{ background: '#E6F4F2', color: 'var(--radar)', padding: '.1rem .4rem', fontSize: '.62rem', fontFamily: "'DM Mono',monospace", borderRadius: '2px' }}>
                                        {parsedRadarResponse.RadarForwardResponse.Addresses[0].Confidence}
                                    </span>
                                )}
                                Confidence: {parsedRadarResponse?.RadarForwardResponse?.Addresses?.[0]?.Confidence || 'N/A'}
                            </div>
                            <a className="maps-link" style={{ color: 'var(--radar)' }} href={`https://maps.google.com/?q=${address.radarLatitude},${address.radarLongitude}`} target="_blank" rel="noreferrer">
                                ↗ Open in Google Maps
                            </a>
                        </div>

                        {/* Delta */}
                        {deltaInfo && (
                            <div className="coord-cell delta-cell">
                                <div className="delta-label">Coordinate Delta</div>
                                <div className="delta-number">{deltaInfo.meters}<span className="delta-unit">m</span></div>
                                <div className="delta-feet">≈ {deltaInfo.feet} feet</div>
                                <div className="delta-divider"></div>
                                <div className="delta-sub-row">
                                    Δlat: {deltaInfo.dLat}°<br />
                                    Δlon: {deltaInfo.dLon}°<br />
                                    Source used: {anonymizeSource(address.coordinateSource)}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="section-head">Map Preview — Provider 1 coordinate (zoom {mapZoom})</div>
                    <div className="map-wrap mb p-0 overflow-hidden" style={{ height: '400px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                        <div className="map-bar" style={{ borderBottom: '1px solid #eee', padding: '8px 12px' }}>
                            <div className="map-legend" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <span className="dot" style={{ backgroundColor: '#34a851' }}></span>
                                <span>
                                    <strong>Provider 1:</strong> {address.latitude ? address.latitude.toFixed(6) : 'N/A'}, {address.longitude ? address.longitude.toFixed(6) : 'N/A'}
                                </span>
                            </div>
                            {deltaInfo && (
                                <div className="map-legend" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span className="dot" style={{ backgroundColor: '#000257' }}></span>
                                    <span>
                                        <strong>Provider 2:</strong> {address.radarLatitude ? address.radarLatitude.toFixed(6) : 'N/A'}, {address.radarLongitude ? address.radarLongitude.toFixed(6) : 'N/A'} &nbsp;·&nbsp; {deltaInfo.meters}m apart
                                    </span>
                                </div>
                            )}
                        </div>
                        <ComparisonMap
                            googleCoordinates={
                                address.latitude && address.longitude
                                    ? { lat: address.latitude, lng: address.longitude }
                                    : undefined
                            }
                            radarCoordinates={
                                address.radarLatitude && address.radarLongitude
                                    ? { lat: address.radarLatitude, lng: address.radarLongitude }
                                    : undefined
                            }
                            address={`${address.street1}, ${address.city}, ${address.stateCode} ${address.zip}`}
                            height="360px"
                            initialMapType="satellite"
                            hideControls={true}
                            zoom={mapZoom}
                            googleLabel="Provider 1"
                            radarLabel="Provider 2"
                            useColoredMarkers={true}
                            googleMarkerColor="#34a851"
                            radarMarkerColor="#000257"
                        />
                    </div>

                    {/* VALIDATION RESULTS */}
                    <div className="section-head">Validation Results</div>
                    <div className="val-grid mb">
                        <div className="val-panel">
                            <div className="val-panel-head"><span className="dot" style={{ backgroundColor: '#34a851' }}></span> Provider 1 Validation API</div>
                            <table className="kv">
                                <tbody>
                                    <tr><td>Status</td><td><span className={`tag ${address.isGoogleValid ? 'tag-green' : 'tag-orange'}`}>{address.isGoogleValid ? 'VALID' : 'INVALID'}</span></td></tr>
                                    <tr><td>Next Action</td><td><span className="tag tag-green">{address.googlePossibleNextAction || 'N/A'}</span></td></tr>
                                    <tr><td>Granularity</td><td>{parsedGoogleResponse?.Result?.Verdict?.ValidationGranularity || 'N/A'}</td></tr>
                                    <tr><td>Inferred Components</td><td>{parsedGoogleResponse?.Result?.Verdict?.HasInferredComponents ? 'Yes' : 'No'}</td></tr>

                                    <tr><td>Formatted Address</td><td style={{ fontSize: '.6rem' }}>{address.googleFormattedAddress || 'N/A'}</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="val-panel">
                            <div className="val-panel-head"><span className="dot" style={{ backgroundColor: '#000257' }}></span> Provider 2 Validation API</div>
                            <table className="kv">
                                <tbody>
                                    <tr><td>Status</td><td><span className={`tag ${address.isRadarValid ? 'tag-teal' : 'tag-orange'}`}>{address.isRadarValid ? 'VERIFIED' : 'UNVERIFIED'}</span></td></tr>
                                    <tr><td>Score</td><td>{address.radarScore ?? 'N/A'} / 100</td></tr>
                                    <tr><td>Confidence</td><td>{parsedRadarResponse?.RadarForwardResponse?.Addresses?.[0]?.Confidence || 'N/A'}</td></tr>
                                    <tr><td>Property Type</td><td>{radarData?.Metadata?.PropertyType || 'N/A'}</td></tr>
                                    <tr><td>Plus4</td><td>{address.zipSuffix || radarData?.Plus4 || 'N/A'}</td></tr>

                                    <tr><td>Formatted Address</td><td style={{ fontSize: '.6rem' }}>{address.radarFormattedAddress || 'N/A'}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* USPS + LOGISTICS */}
                    <div className="section-head">USPS & Logistics Intelligence</div>
                    <div className="bottom-grid">
                        <div className="panel">
                            <div className="panel-head">USPS DPV Flags</div>
                            <div className="panel-body">
                                <table className="kv">
                                    <tbody>
                                        <tr><td>DPV Confirmation</td><td><span className="tag tag-green">{uspsData?.DpvConfirmation === 'Y' ? 'Y — Deliverable' : uspsData?.DpvConfirmation}</span></td></tr>
                                        <tr><td>Vacant</td><td><span className={`tag ${uspsData?.DpvVacant === 'Y' ? 'tag-orange' : 'tag-green'}`}>{uspsData?.DpvVacant === 'Y' ? 'Y — Vacant' : 'N — Occupied'}</span></td></tr>
                                        <tr><td>CMRA / P.O. Box</td><td><span className="tag tag-green">{uspsData?.DpvCmra === 'Y' ? 'Y' : 'N'}</span></td></tr>
                                        <tr><td>No-Stat</td><td><span className="tag tag-green">{uspsData?.DpvNoStat === 'Y' ? 'Y' : 'N'}</span></td></tr>
                                        <tr><td>Enhanced Delivery</td><td><span className="tag tag-green">{uspsData?.DpvEnhancedDeliveryCode || 'N/A'}</span></td></tr>
                                        <tr><td>Door Not Accessible</td><td><span className={`tag ${uspsData?.DpvDoorNotAccessible === 'Y' ? 'tag-orange' : 'tag-green'}`}>{uspsData?.DpvDoorNotAccessible === 'Y' ? 'Y' : 'N'}</span></td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="panel">
                            <div className="panel-head">Logistics Details</div>
                            <div className="panel-body">
                                <table className="kv">
                                    <tbody>
                                        <tr><td>Carrier Route</td><td>{uspsData?.CarrierRoute || 'N/A'}</td></tr>
                                        <tr><td>Route Type</td><td>{uspsData?.CarrierRouteIndicator || 'N/A'}</td></tr>
                                        <tr><td>Record Type</td><td>{uspsData?.AddressRecordType || 'N/A'}</td></tr>
                                        <tr><td>County</td><td>{uspsData?.County || 'N/A'}</td></tr>
                                        <tr><td>Is Apartment</td><td><span className={`tag ${address.isApartment ? 'tag-orange' : 'tag-green'}`}>{address.isApartment ? 'Yes' : 'No'}</span></td></tr>
                                        <tr><td>GeoFence Confidence</td><td><span className="tag tag-blue">{address.geoFenceConfidence || 'N/A'}</span></td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* NAV POINT */}
                    <div className="section-head">Navigation Access Point — Provider 1 Destinations API</div>
                    <div className="nav-panel mb">
                        <div className="panel-head">Driveable / Walkable Entry</div>
                        <div className="nav-inner">
                            <div className="nav-field">
                                <label>Latitude</label>
                                <div className="val">{navPoint?.location?.latitude || 'N/A'}</div>
                            </div>
                            <div className="nav-field">
                                <label>Longitude</label>
                                <div className="val">{navPoint?.location?.longitude || 'N/A'}</div>
                            </div>
                            <div className="nav-field">
                                <label>Street</label>
                                <div className="val">{navPoint?.displayName?.text || 'N/A'}</div>
                            </div>
                            <div className="nav-field">
                                <label>Travel Modes</label>
                                <div className="val">{navPoint?.travelModes?.join(' · ') || 'N/A'}</div>
                            </div>
                        </div>
                    </div>


                    {/* SUMMARY SECTION */}
                    {summary && (
                        <div className="section-head">AI Generated Overview</div>
                    )}
                    {summary && (() => {
                        let parsedSummary;
                        try {
                            parsedSummary = JSON.parse(summary);
                        } catch (e) {
                            // Fallback for plain text if parsing fails
                            return (
                                <div className="mb-4 p-4 bg-light border rounded" style={{ whiteSpace: 'pre-wrap', fontFamily: 'sans-serif', lineHeight: '1.6' }}>
                                    {summary}
                                </div>
                            );
                        }

                        return (
                            <div className="panel mb">
                                <div className="panel-head">Executive Summary Analysis</div>
                                <div className="panel-body">
                                    <table className="kv">
                                        <tbody>
                                            <tr>
                                                <td style={{ width: '20%', verticalAlign: 'top', color: 'var(--ink-light)', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem' }}>Deliverable</td>
                                                <td style={{ textAlign: 'left', fontFamily: 'sans-serif', lineHeight: '1.5', fontSize: '0.8rem' }}>
                                                    {parsedSummary.deliverable}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={{ width: '20%', verticalAlign: 'top', color: 'var(--ink-light)', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem' }}>Discrepancy</td>
                                                <td style={{ textAlign: 'left', fontFamily: 'sans-serif', lineHeight: '1.5', fontSize: '0.8rem' }}>
                                                    {parsedSummary.discrepancy}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={{ width: '20%', verticalAlign: 'top', color: 'var(--ink-light)', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem' }}>Recommendation</td>
                                                <td style={{ textAlign: 'left', fontFamily: 'sans-serif', lineHeight: '1.5', fontSize: '0.8rem' }}>
                                                    {parsedSummary.recommendation}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })()}

                </div>



                {/* FOOTER */}
                <div className="footer">
                    <div className="footer-left">
                        BetterTrucks CheckPoint Engine · Address ID #{address.id}<br />
                        Trace: {address.traceId}
                    </div>
                    <div className="footer-right">
                        Created: {new Date(address.createdAt).toLocaleString()}<br />
                        Validation Source: {anonymizeSource(address.validationSource)}
                    </div>
                </div>
            </div>

            <div className="no-print" style={{ position: 'fixed', bottom: '2rem', right: '2rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <button
                    className="fab"
                    onClick={handleSummarize}
                    disabled={isSummarizing}
                    style={{ position: 'static', background: isSummarizing ? '#ccc' : '#4a148c' }} // Different color for distinction
                >
                    {isSummarizing ? 'GENERATING...' : '✨ SUMMARIZE REPORT'}
                </button>
                <button className="fab" onClick={() => window.print()} style={{ position: 'static' }}>⎙ PRINT / PDF</button>
            </div>
        </div>
    );
};

export default AddressReportManagement;
